import React, { useMemo, useState, useRef } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, Legend
} from 'recharts';
import { 
  ArrowLeft, ExternalLink, Image as ImageIcon, Video, Heart, MessageCircle, Play, Activity, RefreshCw, Info, User, Plus, Trash2, Loader2, Upload, Download, Target
} from 'lucide-react';

// ... (rest of imports)
import { fetchPostInfo, getProxiedImage } from '../../utils/instagramTrackerApi';
import { toast } from 'sonner';

export default function TrackerDetail({ influencer, ratePerReel, onClose, onRefresh, onUpdateAds }) {
  const p = influencer.profile || {};
  const c = influencer.computed || {};
  const posts = influencer.posts || [];
  const history = influencer.history || [];
  
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const growth = useMemo(() => {
    if (history.length < 2) return null;
    const latest = history[history.length - 1];
    const previous = history[history.length - 2];
    const diff = latest.followers - previous.followers;
    const pct = (diff / previous.followers) * 100;
    return { count: diff, pct: pct.toFixed(1) };
  }, [history]);

  const lastSyncTime = history[history.length - 1]?.timestamp;

  const chartData = useMemo(() => {
    return posts.filter(post => post.date).sort((a,b) => new Date(a.date) - new Date(b.date)).map(post => ({
      date: new Date(post.date).toLocaleDateString(undefined, {month:'short', day:'numeric'}),
      views: post.reelViews || 0,
      engagement: post.likes + post.comments,
      format: post.format
    }));
  }, [posts]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-salty-border">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-salty-purple" />
            Live Analytics Tracker
          </h2>
          {lastSyncTime && (
            <span className="px-2 py-0.5 bg-salty-bg border border-salty-border rounded text-[10px] font-bold text-salty-text-muted uppercase tracking-wider">
              Last synced: {getTimeAgo(lastSyncTime)}
            </span>
          )}
        </div>
        <div className="flex gap-3">
          <button onClick={onRefresh} className="px-4 py-2 bg-salty-card border border-salty-border hover:bg-salty-subtle text-salty-text-main text-sm font-medium rounded-lg transition-colors flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-salty-purple" />
            Refresh Data
          </button>
          <button onClick={onClose} className="px-4 py-2 bg-salty-subtle hover:bg-salty-border text-salty-text-main border border-salty-border text-sm font-medium rounded-lg transition-colors">
            Close
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        
        {/* Profile Card */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-salty-card rounded-2xl border border-salty-border p-6 text-center">
            {p.profilePicUrl ? (
              <img 
                src={getProxiedImage(p.profilePicUrl)} 
                alt={p.username} 
                className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-salty-border bg-salty-bg/80 object-cover" 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(p.fullName || p.username)}&background=2A2E39&color=fff&size=200`;
                }}
              />
            ) : (
              <div className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-salty-border bg-salty-bg/80 flex items-center justify-center">
                <User className="w-10 h-10 text-salty-text-muted" />
              </div>
            )}
            <h2 className="text-xl font-bold text-white">{p.fullName}</h2>
            <a href={`https://instagram.com/${p.username}`} target="_blank" rel="noopener noreferrer" className="text-salty-purple hover:underline flex justify-center items-center gap-1 mb-4">
              @{p.username} <ExternalLink className="w-3 h-3" />
            </a>
            <p className="text-sm text-salty-text-muted mb-6 line-clamp-3">{p.biography}</p>
            
            <div className="grid grid-cols-2 gap-4 text-left">
              <div>
                <div className="text-xs text-salty-text-muted uppercase">Followers</div>
                <div className="flex items-baseline gap-1.5">
                  <div className="text-xl font-bold text-white">{formatVal(p.followers)}</div>
                  {growth && (
                    <div className={`text-[10px] font-bold ${growth.count >= 0 ? 'text-salty-teal' : 'text-salty-rose'}`}>
                      {growth.count >= 0 ? '+' : ''}{formatVal(growth.count)} ({growth.pct}%)
                    </div>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-salty-text-muted uppercase">Following</div>
                <div className="text-xl font-bold text-white">{formatVal(p.following)}</div>
              </div>
              <div>
                <div className="text-xs text-salty-text-muted uppercase">Posts</div>
                <div className="text-xl font-bold text-white">{formatVal(p.totalPosts)}</div>
              </div>
              <div>
                <div className="text-xs text-salty-text-muted uppercase">Category</div>
                <div className="text-lg font-bold text-salty-purple">{c.primaryCategory || 'Lifestyle'}</div>
              </div>
            </div>
          </div>

          {/* Follower Growth Chart */}
          {influencer.history && influencer.history.length > 0 && (
            <div className="bg-salty-card rounded-2xl border border-salty-border p-5 mt-6">
              <h3 className="text-sm font-bold text-salty-text-muted uppercase tracking-wider mb-4">Follower Trend</h3>
              <div className="h-[180px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={influencer.history}>
                    <XAxis dataKey="date" stroke="var(--salty-text-muted)" fontSize={10} tickLine={false} axisLine={false} />
                    <YAxis dataKey="followers" domain={['auto', 'auto']} stroke="var(--salty-text-muted)" fontSize={10} tickLine={false} axisLine={false} tickFormatter={formatVal} width={40} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'var(--salty-card)', border: '1px solid var(--salty-border)', borderRadius: '8px', color: 'var(--salty-text-main)', fontSize: '12px' }}
                      labelStyle={{ color: 'var(--salty-text-muted)' }}
                      formatter={(val) => [formatVal(val), 'Followers']}
                    />
                    <Line type="monotone" dataKey="followers" stroke="var(--salty-purple)" strokeWidth={2} dot={{ fill: 'var(--salty-purple)', r: 3 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* Analytics & Content */}
        <div className="lg:col-span-3 space-y-6">
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard title="Engagement Rate" value={`${c.engagementRate}%`} desc="Avg interaction / followers" color="emerald" info="Average sum of likes and comments divided by total followers." />
            <StatCard title="Avg Reel Views" value={formatVal(c.avgReelViews)} desc="From last 12 posts" color="blue" info="Average views organically generated on their recently published reels." />
            <StatCard title="Avg Likes" value={formatVal(c.avgLikes)} desc="Per post" color="pink" info="Average absolute likes on their recent posts." />
            <StatCard title="Viral Ratio" value={`${c.viralPostRatio}%`} desc="Posts > 2x avg views" color="purple" info="Percentage of recent posts that generate more than twice the average views." />
          </div>

          {/* Consistency Metrics */}
          <div className="bg-salty-card border border-salty-border rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-6">Consistency & Output</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-salty-bg/80 p-4 rounded-xl border border-salty-border">
                <div className="flex items-center gap-1 text-xs text-salty-text-muted uppercase tracking-wider mb-1">
                  Posts / Week
                  <InfoTip text="Estimated number of posts published per week based on recent history." />
                </div>
                <div className="text-2xl font-bold text-white">{c.postsPerWeek || '0'}</div>
              </div>
              <div className="bg-salty-bg/80 p-4 rounded-xl border border-salty-border">
                <div className="flex items-center gap-1 text-xs text-salty-text-muted uppercase tracking-wider mb-1">
                  Reels / Week
                  <InfoTip text="Estimated number of Reels explicitly published per week." />
                </div>
                <div className="text-2xl font-bold text-white">{c.reelsPerWeek || '0'}</div>
              </div>
              <div className="bg-salty-bg/80 p-4 rounded-xl border border-salty-border">
                <div className="flex items-center gap-1 text-xs text-salty-text-muted uppercase tracking-wider mb-1">
                  Avg Gap
                  <InfoTip text="Average time between publishing posts (in hours)." />
                </div>
                <div className="text-2xl font-bold text-white">{c.avgTimeBetween || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Salty Ad Tracker */}
          <AdTracker influencer={influencer} onUpdateAds={onUpdateAds} accountEr={c.engagementRate} ratePerReel={ratePerReel || 0} />

          {/* Charts */}
           <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
             <div className="bg-salty-card border border-salty-border rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-6">Views Performance</h3>
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--salty-border)" vertical={false} />
                    <XAxis dataKey="date" stroke="var(--salty-text-muted)" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="var(--salty-text-muted)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={formatVal} />
                    <Tooltip 
                      cursor={{fill: 'var(--salty-border)'}}
                      contentStyle={{ backgroundColor: 'var(--salty-card)', border: '1px solid var(--salty-border)', borderRadius: '8px', color: 'var(--salty-text-main)' }}
                    />
                    <Bar dataKey="views" fill="var(--salty-purple)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-salty-card border border-salty-border rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-6">Engagement Trend (Growth)</h3>
              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--salty-border)" vertical={false} />
                    <XAxis dataKey="date" stroke="var(--salty-text-muted)" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="var(--salty-text-muted)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={formatVal} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'var(--salty-card)', border: '1px solid var(--salty-border)', borderRadius: '8px', color: 'var(--salty-text-main)' }}
                    />
                    <Line type="monotone" dataKey="engagement" stroke="var(--salty-teal)" strokeWidth={3} dot={{ fill: 'var(--salty-teal)', r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="bg-salty-card border border-salty-border rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-6">Recent Posts Preview</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {posts.map(post => (
                <a 
                  key={post.id} 
                  href={post.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group block relative bg-salty-bg/80 rounded-xl border border-salty-border hover:border-salty-purple transition-colors"
                >
                  <div className="aspect-square bg-salty-border relative rounded-t-xl overflow-hidden">
                    {post.thumbnailUrl ? (
                      <img src={getProxiedImage(post.thumbnailUrl)} alt="Thumbnail" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-salty-text-muted" />
                      </div>
                    )}
                    <div className="absolute top-2 right-2 bg-black/60 rounded p-1">
                      {post.format === 'Reel' || post.format === 'Video' ? <Video className="w-3 h-3 text-white" /> : <ImageIcon className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="flex flex-wrap gap-3 items-center text-xs text-salty-text-muted mb-2 font-medium">
                      <div className="flex items-center gap-1" title="Likes">
                        <Heart className="w-3 h-3 text-pink-400" /> {formatVal(post.likes)}
                      </div>
                      <div className="flex items-center gap-1" title="Comments">
                        <MessageCircle className="w-3 h-3 text-salty-text-muted" /> {formatVal(post.comments)}
                      </div>
                      {(post.format === 'Reel' || post.format === 'Video' || post.reelViews > 0) && (
                        <div className="flex items-center gap-1" title="Views">
                          <Play className="w-3 h-3 text-salty-purple" /> {formatVal(post.reelViews)}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                        post.format === 'Reel' ? 'bg-salty-purple/15 text-salty-purple' :
                        post.format === 'Carousel' ? 'bg-salty-purple/15 text-salty-purple' :
                        'bg-salty-border/30 text-salty-text-muted'
                      }`}>
                        {post.format}
                      </span>
                    </div>
                    <p className="text-xs text-salty-text-main line-clamp-2">{post.caption}</p>
                    
                    {/* Performance beneath post */}
                    <div className="mt-3 pt-3 border-t border-salty-border flex items-center justify-between">
                      <div className="flex items-center gap-1 text-xs text-salty-text-muted">
                        ER Rating
                        <InfoTip text="Engagement Rate per post — (Likes + Comments) / Followers × 100. Green = above account average, Amber = below." />
                      </div>
                      <div className={`text-xs font-bold ${parseFloat(post.engagementRate) >= parseFloat(c.engagementRate) ? 'text-salty-teal' : 'text-salty-amber'}`}>
                        {post.engagementRate}%
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
}

function AdTracker({ influencer, onUpdateAds, accountEr, ratePerReel }) {
  const [adUrl, setAdUrl] = useState('');
  const [isBulk, setIsBulk] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const fileInputRef = useRef(null);
  const ads = influencer.trackedAds || [];

  const reelAdsWithViews = ads.filter(a => (a.reelViews || 0) > 0);
  const totalViews = reelAdsWithViews.reduce((s, a) => s + (a.reelViews || 0), 0);
  const totalSpend = ratePerReel * reelAdsWithViews.length;
  const overallCpv = ratePerReel > 0 && totalViews > 0 ? totalSpend / totalViews : null;

  const handleTrackAds = async (targetUrls) => {
    const urls = targetUrls || (isBulk 
      ? adUrl.split('\n').map(u => u.trim()).filter(u => u && (u.includes('instagram.com/p/') || u.includes('instagram.com/reel/')))
      : [adUrl.trim()]);

    if (urls.length === 0) {
      toast.error('Please enter valid Instagram post/reel URLs');
      return;
    }

    setLoading(true);
    // ... rest of logic stays same but uses 'urls'
    setProgress({ current: 0, total: urls.length });
    
    const results = [];
    const currentAds = [...ads];

    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];
      setProgress({ current: i + 1, total: urls.length });
      
      try {
        // Step 1: Extract shortcode
        let shortcode = url;
        const parts = url.split('/').filter(p => p !== '');
        const pidx = parts.indexOf('p') > -1 ? parts.indexOf('p') : parts.indexOf('reel');
        if (pidx > -1 && parts[pidx + 1]) {
          shortcode = parts[pidx + 1].split('?')[0];
        }

        // Avoid duplicates
        if (currentAds.some(a => a.id === shortcode || a.url?.includes(shortcode))) {
          continue; 
        }

        // Step 2: Local check
        const existingPost = influencer.posts?.find(p => p.url?.includes(shortcode) || p.id === shortcode);
        if (existingPost) {
          currentAds.push({ ...existingPost, tracked_at: new Date().toISOString() });
          continue;
        }

        // Step 3: API Fetch
        const postData = await fetchPostInfo(url);
        currentAds.push({ ...postData, tracked_at: new Date().toISOString() });

        // Step 4: Small delay between API calls
        if (i < urls.length - 1) {
          await new Promise(r => setTimeout(r, 800));
        }
      } catch (err) {
        console.error(`Failed to fetch ${url}`, err);
        toast.error(`Failed to fetch: ${url.substring(0, 30)}...`);
      }
    }

    onUpdateAds(currentAds);
    setAdUrl('');
    setLoading(false);
    toast.success(`Tracking ${urls.length} ads updated!`);
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const rows = text.split('\n');
      const urls = rows.map(row => {
        // Handle comma or newline separated
        return row.split(',')[0].replace(/"/g, '').trim();
      }).filter(u => u && (u.includes('instagram.com/p/') || u.includes('instagram.com/reel/')));
      
      if (urls.length > 0) {
        handleTrackAds(urls);
      } else {
        toast.error('No valid Instagram URLs found in CSV');
      }
    };
    reader.readAsText(file);
    e.target.value = null; // Reset input
  };

  const handleRemoveAd = (id) => {
    const newAds = ads.filter(a => a.id !== id);
    onUpdateAds(newAds);
    toast.success('Ad removed from tracker');
  };

  return (
    <div className="bg-salty-card border border-salty-border rounded-2xl p-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h3 className="text-lg font-bold text-salty-text-main">Campaign Ads</h3>
            <button 
              onClick={() => setIsBulk(!isBulk)}
              className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded transition-colors ${isBulk ? 'bg-salty-purple text-white' : 'bg-salty-border text-salty-text-muted hover:text-salty-text-main'}`}
            >
              {isBulk ? 'Bulk Mode ON' : 'Single Add'}
            </button>
          </div>
          <p className="text-xs text-salty-text-muted">Track individual campaign posts and measure their isolated performance.</p>
        </div>
        
        <div className="flex flex-col md:flex-row gap-3 flex-1 lg:max-w-2xl">
          <div className="flex-1 relative flex flex-col gap-2">
            {isBulk ? (
              <textarea 
                placeholder="Paste multiple Instagram URLs (one per line)..." 
                value={adUrl}
                onChange={(e) => setAdUrl(e.target.value)}
                disabled={loading}
                className="w-full min-h-[42px] max-h-[120px] bg-salty-bg/80 border border-salty-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-salty-purple scrollbar-hide"
              />
            ) : (
              <input 
                type="text" 
                placeholder="Paste Instagram Ad URL..." 
                value={adUrl}
                onChange={(e) => setAdUrl(e.target.value)}
                disabled={loading}
                className="w-full h-[42px] bg-salty-bg/80 border border-salty-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-salty-purple"
              />
            )}
            
            {isBulk && (
              <div className="flex items-center justify-between">
                <a 
                  href="/ads_template.csv" 
                  download 
                  className="text-[10px] text-salty-purple hover:underline flex items-center gap-1 font-bold uppercase tracking-wider"
                >
                  <Download className="w-3 h-3" />
                  Download Template
                </a>
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="text-[10px] text-salty-text-muted hover:text-white flex items-center gap-1 font-bold uppercase tracking-wider transition-colors"
                >
                  <Upload className="w-3 h-3" />
                  Upload CSV
                </button>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleCsvUpload} 
                  accept=".csv" 
                  className="hidden" 
                />
              </div>
            )}
          </div>
          
          <button 
            onClick={() => handleTrackAds()} 
            disabled={loading || (!adUrl.trim() && isBulk === false)}
            className="h-[42px] px-6 bg-salty-purple hover:bg-salty-purple-dark disabled:opacity-50 text-white text-sm font-bold rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap min-w-[140px]"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{isBulk ? `${progress.current}/${progress.total}` : 'Fetching...'}</span>
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                <span>{isBulk ? 'Track All Ads' : 'Track Ad'}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* CPV Summary widget */}
      {overallCpv !== null && (
        <div className="mb-5 flex items-center gap-6 px-5 py-3 bg-salty-bg/60 border border-salty-border rounded-xl w-fit">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest font-bold text-salty-text-muted">Rate / Reel</span>
            <span className="text-sm font-bold font-mono text-salty-text-main">₹{ratePerReel.toLocaleString()}</span>
          </div>
          <div className="w-px h-8 bg-salty-border" />
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest font-bold text-salty-text-muted">Total Views</span>
            <span className="text-sm font-bold font-mono text-salty-text-main">{totalViews.toLocaleString()}</span>
          </div>
          <div className="w-px h-8 bg-salty-border" />
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest font-bold text-salty-text-muted">Cost Per View</span>
            <span className={`text-sm font-black font-mono tabular-nums ${overallCpv < 0.5 ? 'text-emerald-400' : overallCpv < 2 ? 'text-salty-purple' : 'text-amber-400'}`}>
              ₹{overallCpv < 1 ? overallCpv.toFixed(3) : overallCpv.toFixed(2)}
              <span className="text-[10px] ml-1 font-semibold">{overallCpv < 0.5 ? '↑ great' : overallCpv < 2 ? 'good' : '↓ high'}</span>
            </span>
          </div>
        </div>
      )}

      {ads.length === 0 ? (
        <div className="py-8 text-center bg-salty-bg/80 rounded-xl border border-dashed border-salty-border">
          <Play className="w-8 h-8 text-salty-border mx-auto mb-2" />
          <p className="text-sm text-salty-text-muted">No ads tracked for this influencer yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ads.map(ad => {
            const isReel = ad.format === 'Reel';
            const avgViews = parseFloat(influencer.computed?.avgReelViews) || 1;
            const accountEr = parseFloat(influencer.computed?.engagementRate) || 1;
            
            let performance = 0;
            const adEr = ((ad.likes + ad.comments) / (influencer.profile?.followers || 1) * 100).toFixed(2);
            
            if (isReel && ad.reelViews > 0 && avgViews > 1) {
              performance = (ad.reelViews / avgViews).toFixed(1);
            } else {
              performance = (parseFloat(adEr) / (parseFloat(accountEr) || 1)).toFixed(1);
            }
            
            return (
              <div key={ad.id} className="relative group bg-salty-bg/80 rounded-xl border border-salty-border hover:border-salty-purple/50 transition-colors p-4">
                {!ad.is_campaign_link && (
                  <button 
                    onClick={() => handleRemoveAd(ad.id)}
                    className="absolute top-2 right-2 p-1 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                
                <div className="flex gap-4">
                  <div className="w-16 h-16 rounded-lg bg-salty-border overflow-hidden flex-shrink-0 flex items-center justify-center">
                    {ad.thumbnailUrl ? (
                      <img src={getProxiedImage(ad.thumbnailUrl)} alt="" className="w-full h-full object-cover" />
                    ) : (

                      <Video className="w-6 h-6 text-salty-text-muted" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] font-bold uppercase py-0.5 px-1.5 bg-salty-purple/10 text-salty-purple rounded">
                        {ad.format || 'Post'}
                      </span>

                      {ad.is_campaign_link && (
                        <span className="text-[10px] font-bold uppercase py-0.5 px-1.5 bg-salty-purple/20 text-salty-purple rounded flex items-center gap-1 max-w-[150px] truncate" title={ad.campaign_name}>
                          <Target className="w-2.5 h-2.5 flex-shrink-0" />
                          {ad.campaign_name || 'Campaign'}
                        </span>
                      )}

                      {ad.url && (
                        <a href={ad.url} target="_blank" rel="noreferrer" className="text-salty-text-muted hover:text-white">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>

                    <p className="text-xs text-salty-text-main font-medium line-clamp-2 min-h-[32px] mb-2" title={ad.caption}>
                      {ad.caption || 'Tracked Ad'}
                    </p>
                    
                    <div className="flex items-center gap-3 text-[10px] font-bold text-slate-400">
                      <div className="flex items-center gap-1"><Heart className="w-3 h-3 text-pink-400" /> {formatVal(ad.likes)}</div>
                      <div className="flex items-center gap-1"><MessageCircle className="w-3 h-3 text-salty-text-muted" /> {formatVal(ad.comments)}</div>
                      {ad.reelViews > 0 && (
                        <div className="flex items-center gap-1"><Play className="w-3 h-3 text-salty-purple" /> {formatVal(ad.reelViews)}</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-salty-border flex items-center justify-between">
                  <div className="text-[10px] text-salty-text-muted font-bold uppercase tracking-wider">Campaign ER</div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-white">{adEr}%</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-black ${
                      parseFloat(performance) >= 1.2 ? 'bg-salty-teal/10 text-salty-teal' :
                      parseFloat(performance) >= 0.8 ? 'bg-salty-amber/10 text-salty-amber' :
                      'bg-rose-500/10 text-rose-400'
                    }`}>
                      {performance}x Avg
                    </span>
                  </div>
                </div>
                {ratePerReel > 0 && ad.reelViews > 0 && (
                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-[10px] text-salty-text-muted font-bold uppercase tracking-wider">CPV</div>
                    {(() => {
                      const cpv = ratePerReel / ad.reelViews;
                      const color = cpv < 0.5 ? 'text-emerald-400' : cpv < 2 ? 'text-salty-purple' : 'text-amber-400';
                      return (
                        <span className={`text-[11px] font-black font-mono ${color}`}>
                          ₹{cpv < 1 ? cpv.toFixed(3) : cpv.toFixed(2)}
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InfoTip({ text }) {
// ... (rest of the helpers stay same)
  return (
    <span className="relative group/tip inline-flex ml-1">
      <Info className="w-3.5 h-3.5 text-salty-text-muted hover:text-salty-purple cursor-help transition-colors" />
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-salty-bg/80 text-salty-text-main text-[11px] leading-relaxed font-normal normal-case tracking-normal px-3 py-2.5 rounded-lg border border-salty-border shadow-xl opacity-0 group-hover/tip:opacity-100 transition-opacity duration-200 z-50">
        {text}
        <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-salty-bg" />
      </span>
    </span>
  );
}

function StatCard({ title, value, desc, color, info }) {
  const colors = {
    emerald: 'text-salty-teal',
    blue: 'text-salty-purple',
    pink: 'text-salty-rose',
    purple: 'text-salty-purple'
  };
  return (
    <div className="bg-salty-card border border-salty-border rounded-2xl p-5 hover:border-salty-purple/50 transition-colors flex flex-col justify-between">
      <div>
        <div className="flex items-center gap-1 text-xs text-salty-text-muted uppercase tracking-wider font-medium mb-1">
          {title}
          {info && <InfoTip text={info} />}
        </div>
        <div className={`text-3xl font-bold ${colors[color]} mb-1`}>{value}</div>
      </div>
      <div className="text-xs text-salty-text-muted mt-2">{desc}</div>
    </div>
  );
}

function formatVal(v) {
  if (!v) return '0';
  if (v >= 1000000) return (v/1000000).toFixed(1) + 'm';
  if (v >= 1000) return (v/1000).toFixed(1) + 'k';
  return v;
}
